import { Injectable } from '@angular/core';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { mapTo, Observable, of } from 'rxjs';
import { catchError, filter, map, pluck, switchMap, take, tap } from 'rxjs/operators';
import { CarrierModel, CustomHttpErrorResponse, Error } from '../../../../../../../common';
import { NotificationService } from '../../../../../../../common/services/notification/notification.service';
import { HubContextFacade, HubItemFacade } from '../../../../../administration';
import { HubBrief } from '../../../../../administration/hub/models/hub.model';
import { UliItemTruck, UliItemOverviewStatuses } from '../../models';
import { UliItemAllocation, UliItemOverviewModel, UliItemUliInfo } from '../../models/uli-item-overview.model';
import { UliItemFacade } from '../uli-item.facade';
import {
  UliItemOverviewCarriersLoad,
  UliItemOverviewDeleteParkingSpot,
  UliItemOverviewInitialState,
  UliItemOverviewLoad,
  UliItemOverviewSapNotify,
  UliItemOverviewStatusesLoad,
  UliItemOverviewUpdate,
  UliItemOverviewUpdateMainInfoDetails,
  UliItemOverviewUpdateParkingSpot,
  UliItemOverviewUpdateTruckDetails,
} from './uli-item-overview.actions';
import { UliItemOverviewService } from './uli-item-overview.service';

export interface UliItemOverviewStateModel {
  overview: UliItemOverviewModel;
  statuses: UliItemOverviewStatuses;
  allocation: UliItemAllocation;
  carriers: CarrierModel[];
  loaded: boolean;
}

const initialState: UliItemOverviewStateModel = {
  overview: null,
  statuses: null,
  allocation: null,
  carriers: null,
  loaded: false,
};

@State<UliItemOverviewStateModel>({
  name: 'uliItemOverview',
  defaults: initialState,
})
@Injectable()
export class UliItemOverviewState {
  constructor(
    public uliItemOverviewService: UliItemOverviewService,
    private notificationService: NotificationService,
    private hubContextFacade: HubContextFacade,
    private hubItemFacade: HubItemFacade,
    private uliItemFacade: UliItemFacade,
  ) {}

  @Selector()
  static getState(state: UliItemOverviewStateModel): UliItemOverviewStateModel {
    return state;
  }

  @Selector()
  static getOverview(state: UliItemOverviewStateModel): UliItemOverviewModel {
    return state.overview;
  }

  @Selector()
  static getAllocation(state: UliItemOverviewStateModel): UliItemAllocation {
    return state.allocation;
  }

  @Selector()
  static getCarriers(state: UliItemOverviewStateModel): CarrierModel[] {
    return state.carriers;
  }

  @Selector()
  static getLoaded(state: UliItemOverviewStateModel): boolean {
    return state.loaded;
  }

  @Action(UliItemOverviewLoad)
  getOverview(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubContextFacade.selectedHub$.pipe(
      filter(Boolean),
      map((hub: HubBrief) => hub.id),
      switchMap((hubId: number) => this.uliItemOverviewService.getUliOverview(payload, hubId)),
      take(1),
      map((overview: UliItemOverviewModel) => {
        ctx.patchState({ overview, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.uliItemFacade.setInitialState();
        return of(this.notificationService.error(error.message));
      }),
    );
  }

  @Action(UliItemOverviewUpdate)
  updateUliOverview(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliItemOverviewService.updateUliOverview(payload.uliItemActionParams, payload.uliItem).pipe(
      take(1),
      map((uliItem: UliItemUliInfo) => {
        this.notificationService.success('api.uli.success.update');
        ctx.patchState({
          overview: { ...ctx.getState().overview, uliInfo: uliItem },
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemOverviewUpdateTruckDetails)
  updateTruckDetails(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewUpdateTruckDetails,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubContextFacade.selectedHub$.pipe(
      filter(Boolean),
      pluck('id'),
      switchMap((hubId: number) =>
        this.uliItemOverviewService.updateTruckDetails(
          hubId,
          payload.compartmentId,
          payload.type,
          payload.uli,
          payload.oldTruckId,
          payload.toUpdate,
        ),
      ),
      take(1),
      tap((truck: UliItemTruck) => {
        const state = ctx.getState();
        ctx.patchState({
          overview: {
            ...state.overview,
            truckInfos: state.overview.truckInfos.map((t: UliItemTruck) =>
              t.truckInformationView.truckId === truck.truckInformationView.truckId ? truck : t,
            ),
          },
          loaded: true,
        });
        this.notificationService.success('api.truck.success.update');
      }),
      mapTo(void 0),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemOverviewUpdateMainInfoDetails)
  updateMainInfoDetails(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewUpdateMainInfoDetails,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliItemOverviewService
      .updateMainInfoDetails(payload.compartmentId, payload.toUpdate, payload.type, payload.uliNumber)
      .pipe(
        take(1),
        map((uliInfo: UliItemUliInfo) => {
          this.notificationService.success('api.uli.success.update');
          payload.callback();
          ctx.patchState({
            overview: { ...ctx.getState().overview, uliInfo },
            loaded: true,
          });
        }),
        catchError(({ error }: CustomHttpErrorResponse<Error>) => {
          payload.callback();
          return of(this.notificationService.error(error.message));
        }),
      );
  }

  @Action(UliItemOverviewStatusesLoad)
  getStatuses(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewStatusesLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubItemFacade.hubId$.pipe(
      filter(Boolean),
      take(1),
      switchMap((hubId: number) => {
        return this.uliItemOverviewService.getUliStatuses(payload, hubId).pipe(
          take(1),
          map((statuses: UliItemOverviewStatuses) => {
            ctx.patchState({ statuses, loaded: true });
          }),
          catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
        );
      }),
    );
  }

  @Action(UliItemOverviewCarriersLoad)
  getCarriers(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewCarriersLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliItemOverviewService.getCarriers(payload).pipe(
      take(1),
      map((carriers: CarrierModel[]) => {
        ctx.patchState({ carriers });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemOverviewSapNotify)
  notifySap(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewSapNotify,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliItemOverviewService.notifySap(payload).pipe(
      take(1),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemOverviewDeleteParkingSpot)
  deleteParkingSpot(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewDeleteParkingSpot,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliItemOverviewService.deleteParkingSpot(payload.hubId, payload.truckId).pipe(
      take(1),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemOverviewUpdateParkingSpot)
  updateParkingSpot(
    ctx: StateContext<UliItemOverviewStateModel>,
    { payload }: UliItemOverviewUpdateParkingSpot,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.uliItemOverviewService.updateParkingSpot(payload.hubId, payload.truckId, payload.parkingSpaceId).pipe(
      take(1),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(UliItemOverviewInitialState)
  setInitialState(ctx: StateContext<UliItemOverviewStateModel>): void {
    ctx.setState(initialState);
  }
}
